// src/api.js
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

let accessToken = null;
let refreshPromise = null;
let queue = []; // [{ resolve, reject, req }]

export function setAccessToken(t) {
  accessToken = t || null;
}

export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  accessToken = null;
}

function clearStoredAuth() {
  clearAccessToken();

  try {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  } catch {}
}

function redirectToLogin() {
  const currentPath =
    window.location.pathname + window.location.search + window.location.hash;

  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign(`/login?next=${encodeURIComponent(currentPath)}`);
  }
}

export function clearAuthAndRedirect() {
  clearStoredAuth();
  redirectToLogin();
}

function resolveQueue(newToken) {
  queue.forEach(({ resolve, req }) => {
    req.headers = {
      ...(req.headers || {}),
      Authorization: `Bearer ${newToken}`,
    };
    resolve(api(req));
  });
  queue = [];
}

function rejectQueue(error) {
  queue.forEach(({ reject }) => reject(error));
  queue = [];
}

const HARD_CODES = new Set([
  "SESSION_MAX_AGE",
  "SESSION_IDLE_TIMEOUT",
  "SESSION_REVOKED",
  "REFRESH_REUSED",
  "REFRESH_EXPIRED",
  "REFRESH_INVALID",
  "NO_REFRESH",
]);

function isAuthRoute(url = "") {
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/logout")
  );
}

async function doRefresh() {
  const { data } = await api.post("/auth/refresh", {});
  const newToken = data?.accessToken || data?.token || null;

  if (!newToken) {
    throw new Error("No access token returned from refresh.");
  }

  setAccessToken(newToken);

  try {
    localStorage.setItem("token", newToken);
  } catch {}

  return newToken;
}

// Request interceptor
api.interceptors.request.use((cfg) => {
  if (accessToken) {
    cfg.headers = {
      ...(cfg.headers || {}),
      Authorization: `Bearer ${accessToken}`,
    };
  }
  return cfg;
});

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const code = error?.response?.data?.code;
    const url = original?.url || "";

    // If refresh/login/logout itself fails with hard auth error, logout cleanly
    if (status === 401 && isAuthRoute(url) && HARD_CODES.has(code)) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // Hard auth failures anywhere -> logout cleanly
    if (status === 401 && HARD_CODES.has(code)) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    // Only soft access-token failures should trigger refresh
    const shouldTryRefresh =
      status === 401 &&
      !original._retry &&
      (code === "ACCESS_TOKEN_EXPIRED" || code === "ACCESS_TOKEN_INVALID");

    if (!shouldTryRefresh) {
      return Promise.reject(error);
    }

    original._retry = true;

    // If refresh already in progress, queue this request
    if (refreshPromise) {
      return new Promise((resolve, reject) => {
        queue.push({ resolve, reject, req: original });
      });
    }

    try {
      refreshPromise = doRefresh();

      const newToken = await refreshPromise;

      resolveQueue(newToken);

      original.headers = {
        ...(original.headers || {}),
        Authorization: `Bearer ${newToken}`,
      };

      return api(original);
    } catch (refreshError) {
      rejectQueue(refreshError);
      clearAuthAndRedirect();
      return Promise.reject(refreshError);
    } finally {
      refreshPromise = null;
    }
  },
);

// Call this once on app boot
export async function bootstrapAuth() {
  try {
    const newToken = await doRefresh();
    return !!newToken;
  } catch {
    clearStoredAuth();
    return false;
  }
}

export async function logout() {
  try {
    await api.post("/auth/logout", {});
  } catch {
  } finally {
    clearAuthAndRedirect();
  }
}
